import { useCallback, useEffect, useState } from 'react';

import { useUnit } from 'effector-react';

import { useCustomerSubscriptionJoinCode } from '@m/hooks/data/customer/subscriptions';
import {
  addLegacySubscriptionToCart,
  addSubscriptionToCart,
  makePaymentForSubscription,
} from '@m/state/subscription';
import { paymentStore } from 'apps/customer/state/payment';

import { buildReviewPricing } from './reviewSubscriptionPricing';

import type { NewSubscriptionCheckoutResponse, Subscription } from '@m/types/api';

type SubscriptionCart =
  | {
      item: Subscription;
      kind: 'legacy';
    }
  | {
      item: NewSubscriptionCheckoutResponse;
      kind: 'selfService';
      planUuid: string;
    };

type CartState =
  | { status: 'idle' | 'loading' }
  | { message: string; status: 'error' }
  | { cart: SubscriptionCart; status: 'ready' };

export type ReviewSubscriptionData =
  | { status: 'loading' }
  | { message: string; retry?: () => void; status: 'error' }
  | {
      address: string;
      dueToday: number;
      isPurchaseDisabled: boolean;
      lotName: string;
      monthlyRate: number;
      monthlyTotal: number;
      nextDue: string;
      onContinue: () => Promise<void>;
      paymentMethod: string;
      planName: string;
      proratedCoverage: number | null;
      proratedPeriod: string;
      serviceFee: number;
      status: 'ready';
      tax: number;
    };

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

function totalFees(fees: { amount?: number | null }[] | undefined): number {
  return (fees ?? []).reduce((total, fee) => total + (fee.amount ?? 0), 0);
}

/** Loads the invited plan, creates its checkout cart, and derives review-screen data. */
export function useReviewSubscriptionData(
  subscriptionPlanId: string | null,
): ReviewSubscriptionData {
  const { defaultPayment } = useUnit(paymentStore);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [cartState, setCartState] = useState<CartState>({ status: 'idle' });
  const [cartAttempt, setCartAttempt] = useState(0);

  const {
    data: subscriptionData,
    refetch: refetchSubscription,
    status: subscriptionStatus,
  } = useCustomerSubscriptionJoinCode(subscriptionPlanId || '');

  const subscription = subscriptionData?.subscriptionPlan;
  const siteDetails = subscriptionData?.siteDetails;
  const planUuid = subscription?.uuid;
  const planId = subscription?.id;
  let cartKey: string | null = null;
  if (siteDetails) {
    if (planUuid) cartKey = `uuid:${planUuid}`;
    else if (planId) cartKey = `id:${planId}`;
  }

  useEffect(() => {
    if (!cartKey) {
      setCartState({ status: 'idle' });
      return undefined;
    }

    let cancelled = false;
    setCartState({ status: 'loading' });

    const loadCart = async () => {
      try {
        if (planUuid) {
          const response = await addSubscriptionToCart({ planUuid });
          if (!response.success || !response.data) {
            throw new Error(response.error || 'Unable to load subscription pricing.');
          }
          if (!cancelled) {
            setCartState({
              status: 'ready',
              cart: { kind: 'selfService', item: response.data, planUuid },
            });
          }
          return;
        }

        if (planId) {
          const response = await addLegacySubscriptionToCart({ planId });
          const item = response.success ? response.data?.subscription : undefined;
          if (!response.success || !item) {
            throw new Error(response.error || 'Unable to load subscription pricing.');
          }
          if (!cancelled) {
            setCartState({ status: 'ready', cart: { kind: 'legacy', item } });
          }
        }
      } catch (error) {
        if (!cancelled) {
          setCartState({
            status: 'error',
            message: errorMessage(error, 'Unable to load subscription pricing.'),
          });
        }
      }
    };

    void loadCart();
    return () => {
      cancelled = true;
    };
  }, [cartAttempt, cartKey, planId, planUuid]);

  const retry = useCallback(() => {
    if (subscriptionStatus === 'error') void refetchSubscription();
    setCartAttempt((attempt) => attempt + 1);
  }, [refetchSubscription, subscriptionStatus]);

  if (subscriptionStatus === 'pending') {
    return { status: 'loading' };
  }

  if (subscriptionStatus === 'error') {
    return { status: 'error', message: 'Unable to load this subscription.', retry };
  }

  if (!subscription || !siteDetails) {
    return { status: 'error', message: 'Subscription details are unavailable.' };
  }

  if (!defaultPayment) {
    return { status: 'error', message: 'A payment method is required to continue.' };
  }

  if (cartState.status === 'error') {
    return { status: 'error', message: cartState.message, retry };
  }

  if (cartState.status !== 'ready') {
    return { status: 'loading' };
  }

  const cart = cartState.cart;
  const selfServiceCart = cart.kind === 'selfService' ? cart.item : null;
  const legacyCart = cart.kind === 'legacy' ? cart.item : null;
  const nextChargeAmount =
    selfServiceCart?.nextChargeAmount ?? legacyCart?.details.nextChargeAmount;
  const nextChargeDate = selfServiceCart?.nextChargeDate ?? legacyCart?.details.nextChargeDate;
  if (nextChargeAmount == null || nextChargeDate == null) {
    return {
      status: 'error',
      message: 'Subscription pricing is unavailable.',
      retry,
    };
  }

  const serviceFee =
    totalFees(selfServiceCart?.siteFees) || totalFees(legacyCart?.details.siteFees);
  const taxCents = selfServiceCart?.totalTaxMonthly ?? legacyCart?.details.totalTaxMonthly ?? 0;
  const monthlyTotalCents =
    selfServiceCart?.totalRateMonthly ?? legacyCart?.details.totalRateMonthly ?? 0;
  const pricing = buildReviewPricing({
    dueTodayCents: nextChargeAmount,
    monthlyRateCents: subscription.details.baseRateMonthly,
    monthlyTotalCents,
    // Site fees come back in dollars; everything else is cents.
    serviceFeeCents: Math.round(serviceFee * 100),
    taxCents,
  });

  const onContinue = async () => {
    setIsPurchasing(true);
    try {
      const params =
        cart.kind === 'selfService'
          ? {
              paymentMethodId: defaultPayment.id,
              selfServiceSubscriptionId: cart.planUuid,
            }
          : {
              paymentMethodId: defaultPayment.id,
              subscriptionId: cart.item.id,
            };
      const response = await makePaymentForSubscription(params);
      if (!response.success) {
        throw new Error(response.error || 'Error during checkout. Please try again.');
      }
    } finally {
      setIsPurchasing(false);
    }
  };

  const locality = [siteDetails.city, siteDetails.state].filter(Boolean).join(', ');
  const address = [siteDetails.street, locality, siteDetails.zip].filter(Boolean).join(' ');

  return {
    status: 'ready',
    lotName: siteDetails.name || '',
    address,
    planName: subscription.details.displayText || siteDetails.name || '',
    monthlyRate: subscription.details.baseRateMonthly / 100,
    serviceFee,
    tax: taxCents / 100,
    monthlyTotal: monthlyTotalCents / 100,
    paymentMethod: `${defaultPayment.cardBrand} •••${defaultPayment.last4Digits}`,
    nextDue: pricing.nextDue,
    dueToday: pricing.dueToday,
    proratedCoverage: pricing.proratedCoverage,
    proratedPeriod: pricing.proratedPeriod,
    onContinue,
    isPurchaseDisabled: isPurchasing,
  };
}
